import Components from '../Components';
import joinClassNames from "../JoinClassNames";

/**
 * An app needs to apply default styling to all its components of a type.
 * Like all buttons need to have some base styling. Instead of passing
 * that base style in to the component every time you use it, CommonAppSetup
 * allows setting up those details one time and all the components will
 * then use it via black magic.
 *
 * Note that since these values are not in the state, changing them at
 * run time will not automatically rerender. The purpose of this setup is
 * a one time pre-app launch setup so that the app renders the same. Any
 * further customization should be performed when rendering.
 *
 *
 * This class was designed to provide future global settings. That is why
 * className is specified for each component so that something like events
 * could be added later.
 *
 *
 * All of the React Common library uses this Singleton CommonAppSetup
 * It records styling classes for the app. This is a one time setup for
 * providing class names to components to be used app wide.
 * For dynamic classes, pass them in the standard way through className=""
 * to the component.
 *
 * When getting classNames, you can pass in the specific element of the
 * component for which to get the classname and it will also add the
 * "all" classes to it.
 *
 * Usage:
 * import {CommonAppSetup} from "dts-react-common"
 *
 * Example #1 - Give all buttons a class:
 * CommonAppSetup.setClassName(Components.BUTTON, 'button', 'my-button-class');
 *
 * Example #2 - Give all checkbox labels a class:
 * CommonAppSetup.setClassName(Components.CHECKBOX, 'label', 'check-box-label');
 *
 * Example #3 - Give InputContainer a couple of classes:
 * CommonAppSetup.setClassName(Components.INPUT_CONTAINER, 'inputContainer', 'class1 class2');
 *
 */
class CommonAppSetup {

	constructor() {
		// the actual data, not exposed to the world
		this.components = {
			[Components.GLOBAL]: {
				className: {
					error: '',
				},
			},
			[Components.AJAX_SPINNER]: {
				className: {
					spinner: '',
					backdrop: '',
					label: '',
					error: '',
					all: ''
				},
			},
			[Components.BUTTON]: {
				className: {
					busy:'',
					button: '',
					all: '',
					wrapper: ''
				},
			},
			[Components.CHECKBOX]: {
				className: {
					label: '',
					input: '',
					all: '',
					wrapper: ''
				}
			},
			[Components.CALENDAR_INPUT]: {
				className: {
					/* label and input are controlled by textInput */
					button: '',
					all: '',
					wrapper: ''
				}
			},
			[Components.COLLAPSIBLE_SECTION]: {
				className: {
					sectionHeader: '',
					sectionContent: '',
					all: ''
				}
			},
			[Components.CONFIRMATION_BUTTON]: {
				className: {
					button: '',
					all: ''
				},
			},
			[Components.INPUT_CONTAINER]: {
				className: {
					inputContainer: '',
					all: ''
				}
			},
			[Components.FORM_ERROR]: {
				className: {
					wrapper: '',
					all: ''
				}
			},
			[Components.LINK]: {
				className: {
					wrapper: '',
					label: '',
					input: '',
					all: ''
				}
			},
			[Components.POP_OVER]: {
				className: {
					wrapper: '',
					anchorWrapper: '',
					popupWrapper: '',
					arrow: '',
					all: '',
				}
			},
			[Components.POPUP_WINDOW]: {
				className: {
					wrapper: '',
					content: '',
					all: '',
				},
			},
			[Components.SEGMENTED_BUTTON]: {
				className: {
					label: '',
					buttonWrapper: '',
					button: '',
					all: '',
					wrapper: '',
				},
			},
			[Components.SELECT_INPUT]: {
				className: {
					label: '',
					input: '',
					all: '',
					wrapper: ''
				}
			},
			[Components.TEXT_AREA]: {
				className: {
					label: '',
					input: '',
					all: '',
					wrapper: ''
				}
			},
			[Components.TEXT_INPUT]: {
				className: {
					label: '',
					input: '',
					all: '',
					wrapper: ''
				}
			},
			[Components.TABLE]: {
				className: {
					container: '',
					table: '',
					all: '',
				}
			},
			[Components.PLAIN_TEXT]: {
				className: {
					label: '',
					text: '',
					all: '',
					wrapper: '',
				}
			},
			[Components.FILE_UPLOAD]: {
				className: {
					input: '',
					label: '',
					all: '',
					wrapper: '',
				},
			},
		};
	}

	/**
	 * Get class name for component's parts
	 *
	 * @param component which component
	 * @param element which element inside the component ('all' will always be added); falsey to get just the "all" classes
	 * @param discludeAll boolean true to not have "all" classes item added; table has a container that would might not want all classes since all maybe be targeted at the innards of the container
	 * @return string space separated string of class names to apply to the component's element
	 */
	getClassName(component, element, discludeAll) {
		const group = this.getComponentGroup(component);

		let className = '';
		if (group !== undefined) {
			const all = discludeAll ? '' : group.className.all;
			const single = element ? this.getElementFromGroup(group.className, element) : '';
			className = joinClassNames(all, single);
		}
		return className;
	}

	/**
	 * set the className for an element
	 *
	 * @param component which component
	 * @param element which element
	 * @param className the new className
	 */
	setClassName(component, element, className) {
		const group = this.getComponentGroup(component);

		// test that the element exists
		this.getElementFromGroup(group.className, element);

		group.className[element] = className;
	}

	/**
	 * get a component group from common app setup
	 * gives console error if not found
	 *
	 * @param component the name of hte component: use Components... constants
	 * @return object for the group or undefined if not found
	 */
	getComponentGroup(component) {
		const group = this.components[component];
		if (group === undefined) {
			console.error(`Unknown Common App Setup component: ${component}`);
		}
		return group;
	}

	/**
	 * get an element from a group; checks for non-existing elements
	 *
	 * @param group the group like a className group
	 * @param element the element's name to lookup
	 * @return string for the className for that element
	 */
	getElementFromGroup(group, element) {
		let single = '';
		if (element) {
			single = group[element];
			if (single === undefined) {
				console.error(`Unknown Common App Setup element: "${element}"`, group);
			}
		}
		return single;
	}
}

export default new CommonAppSetup();
